import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Images } from "../../../utils/Images";

// ─── Animations ───────────────────────────────────────────────────────────────
// Horizontal "push" slide: the outgoing slide and the incoming slide move
// together in lockstep, like a physical filmstrip being pushed sideways.
const SLIDE_DURATION = 550;

const pushOutToLeft = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-100%); }
`;
const pushInFromRight = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;
const pushOutToRight = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(100%); }
`;
const pushInFromLeft = keyframes`
  from { transform: translateX(-100%); }
  to   { transform: translateX(0); }
`;

// ─── Image URL helpers ────────────────────────────────────────────────────────
const IMAGE_BASE = import.meta.env.VITE_API_IMAGE_PATH || "";

const buildImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return IMAGE_BASE ? `${IMAGE_BASE}/${path}` : path;
};

const FALLBACK_IMGS = [Images.heroI2, Images.heroI2, Images.heroI2];

const DEFAULT_POSITIONS = ["50% center", "50% center", "50% center"];

const DUMMY_SLIDES = [
  {
    id: 1,
    mainTitle: "Together We Support\nEducate and Heal",
    description: "Empowering underprivileged students with access to quality education, resources, and opportunities.",
    images: [Images.heroI1, Images.heroI2, Images.heroI1],
    positions: DEFAULT_POSITIONS,
    link: null,
  },
  {
    id: 2,
    mainTitle: "Every Donation\nSaves a Life",
    description: "Your contribution provides critical medical care and treatment to those who cannot afford it.",
    images: [Images.heroI1, Images.heroI2, Images.heroI1],
    positions: DEFAULT_POSITIONS,
    link: null,
  },
  {
    id: 3,
    mainTitle: "Help Us End Hunger\nin Our Communities",
    description: "Thousands go to bed hungry every night. A small act of kindness from you can fill their plate.",
    images: [Images.heroI1, Images.heroI2, Images.heroI1],
    positions: DEFAULT_POSITIONS,
    link: null,
  },
];

// ─── CMS data hook ────────────────────────────────────────────────────────────
const HERO_CMS_ID = 59;

// "x1,y1,x2,y2,x3,y3" → [["x1% y1%"], ["x2% y2%"], ["x3% y3%"]]
// Falls back to "50% 50%" for any missing/invalid value.
const parsePanelPositions = (raw) => {
  const clamp = (v) => (isNaN(v) ? 50 : Math.max(0, Math.min(100, v)));
  if (!raw || typeof raw !== "string") return ["50% 50%", "50% 50%", "50% 50%"];
  const n = raw.split(",").map(Number);
  // Support both old 3-value format ("x,x,x") and new 6-value format ("x,y,x,y,x,y")
  if (n.length >= 6) {
    return [
      `${clamp(n[0])}% ${clamp(n[1])}%`,
      `${clamp(n[2])}% ${clamp(n[3])}%`,
      `${clamp(n[4])}% ${clamp(n[5])}%`,
    ];
  }
  // Legacy 3-value (x-only): use y=50
  return [
    `${clamp(n[0])}% 50%`,
    `${clamp(n[1])}% 50%`,
    `${clamp(n[2])}% 50%`,
  ];
};

const useHeroSlides = () => {
  const home = useSelector((s) => s.cms.home);
  const loading = useSelector((s) => s.cms.loading);

  const slides = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const hero = sections.find((s) => s?.id === HERO_CMS_ID);
    const related = Array.isArray(hero?.relatedCMS) ? hero.relatedCMS : [];
    return related
      .filter((r) => r && (r.status === 1 || r.status === true) && (r.image || r.image2 || r.image3))
      .map((r, i) => {
        const positions = parsePanelPositions(r.stand_title);
        const dImg1 = buildImageUrl(r.image)  || FALLBACK_IMGS[0];
        const dImg2 = buildImageUrl(r.image2) || FALLBACK_IMGS[1];
        const dImg3 = buildImageUrl(r.image3) || FALLBACK_IMGS[2];
        return {
          id: r.id ?? i,
          mainTitle: r.title || "",
          description: r.sub_title || "",
          images: [dImg1, dImg2, dImg3],
          // Dedicated landscape crops for mobile strips; fall back to desktop images
          mobileImages: [
            buildImageUrl(r.image_m1) || dImg1,
            buildImageUrl(r.image_m2) || dImg2,
            buildImageUrl(r.image_m3) || dImg3,
          ],
          positions,
          link: r.link || null,
        };
      });
  }, [home]);

  // Still waiting on the first CMS response — show a shimmer instead of dummy images.
  // Once settled (loaded or failed), fall back to DUMMY_SLIDES only if genuinely empty.
  const isInitialLoading = loading && !home;

  return {
    slides: slides.length ? slides : isInitialLoading ? [] : DUMMY_SLIDES,
    isInitialLoading,
  };
};

// ─── Loading shimmer (matches real banner's box size — no layout shift) ───────
const HeroSkeleton = () => (
  <>
    <Box sx={{ display: { xs: "none", md: "block" }, width: "94.44%", margin: "15px auto" }}>
      <Skeleton variant="rectangular" width="100%" height="649px" sx={{ borderRadius: "20px" }} animation="wave" />
    </Box>
    <Box sx={{ display: { xs: "block", md: "none" }, width: "calc(100% - 20px)", margin: "15px auto" }}>
      <Skeleton
        variant="rectangular"
        width="100%"
        sx={{ aspectRatio: "390 / 480", borderRadius: "14px" }}
        animation="wave"
      />
    </Box>
  </>
);

// ─── Arrow SVGs ───────────────────────────────────────────────────────────────
const ArrowRight = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="39" height="39" rx="19.5" fill="white"/>
    <rect x="0.5" y="0.5" width="39" height="39" rx="19.5" stroke="#E8E8E8"/>
    <path d="M23.7074 20.4939L20.0059 17.0004C19.8308 16.8362 19.7391 16.6308 19.7308 16.3841C19.7226 16.1375 19.8143 15.9249 20.0059 15.7463C20.1811 15.5821 20.404 15.5 20.6746 15.5C20.9453 15.5 21.1681 15.5821 21.3433 15.7463L25.7373 19.8669C25.8328 19.9565 25.9006 20.0535 25.9408 20.158C25.9809 20.2625 26.0006 20.3745 26 20.4939C25.9993 20.6134 25.9796 20.7253 25.9408 20.8299C25.9019 20.9344 25.8341 21.0314 25.7373 21.121L21.3433 25.2415C21.1681 25.4058 20.9494 25.4918 20.687 25.4995C20.4247 25.5073 20.1976 25.4213 20.0059 25.2415C19.8308 25.0773 19.7433 24.8683 19.7433 24.6145C19.7433 24.3607 19.8308 24.1517 20.0059 23.9875L23.7074 20.4939ZM17.9761 20.4939L14.2746 17.0004C14.0995 16.8362 14.0081 16.6308 14.0004 16.3841C13.9928 16.1375 14.0842 15.9249 14.2746 15.7463C14.4497 15.5821 14.6726 15.5 14.9432 15.5C15.2139 15.5 15.4368 15.5821 15.6119 15.7463L20.0059 19.8669C20.1015 19.9565 20.1693 20.0535 20.2094 20.158C20.2495 20.2625 20.2693 20.3745 20.2686 20.4939C20.268 20.6134 20.2483 20.7253 20.2094 20.8299C20.1706 20.9344 20.1027 21.0314 20.0059 21.121L15.6119 25.2415C15.4368 25.4058 15.218 25.4918 14.9557 25.4995C14.6933 25.5073 14.4663 25.4213 14.2746 25.2415C14.0995 25.0773 14.0119 24.8683 14.0119 24.6145C14.0119 24.3607 14.0995 24.1517 14.2746 23.9875L17.9761 20.4939Z" fill="#FF7687"/>
  </svg>
);

const ArrowLeft = () => (
  <Box sx={{ transform: "scaleX(-1)", display: "inline-flex" }}>
    <ArrowRight />
  </Box>
);

// ─── One desktop slide (image panels + text panel) ─────────────────────────────
const DesktopSlide = ({ slideData, onDonate, sx }) => {
  const img1 = slideData.images?.[0] || FALLBACK_IMGS[0];
  const img2 = slideData.images?.[1] || FALLBACK_IMGS[1];
  const img3 = slideData.images?.[2] || FALLBACK_IMGS[2];
  const pos1 = slideData.positions?.[0] || "50% center";
  const pos2 = slideData.positions?.[1] || "50% center";
  const pos3 = slideData.positions?.[2] || "50% center";

  return (
    <Box sx={{ position: "absolute", inset: 0, ...sx }}>
      {/* ══ RIGHT IMAGE PANEL ════════════════════════════════════════════════ */}
      <Box sx={{
        position: "absolute",
        right: 0,
        top: 0,
        width: "57%",
        height: "100%",
        backgroundColor: "transparent",
        overflow: "hidden",
        zIndex: 1,
      }}>
        <Box sx={{
          position: "absolute",
          inset: 0,
          clipPath: "polygon(14.4% 0%, 47.7% 0%, 33.3% 100%, 0% 100%)",
          overflow: "hidden",
        }}>
          <Box sx={{ position: "absolute", top: 0, left: "0%", width: "47.7%", height: "100%" }}>
            <img src={img1} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: pos1, display: "block" }} />
          </Box>
        </Box>

        <Box sx={{
          position: "absolute",
          inset: 0,
          clipPath: "polygon(47.7% 0%, 81.1% 0%, 66.7% 100%, 33.3% 100%)",
          overflow: "hidden",
        }}>
          <Box sx={{ position: "absolute", top: 0, left: "33.3%", width: "47.8%", height: "100%" }}>
            <img src={img2} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: pos2, display: "block" }} />
          </Box>
        </Box>

        <Box sx={{
          position: "absolute",
          inset: 0,
          clipPath: "polygon(81.1% 0%, 114.4% 0%, 100% 100%, 66.7% 100%)",
          overflow: "hidden",
        }}>
          <Box sx={{ position: "absolute", top: 0, left: "66.7%", width: "47.7%", height: "100%" }}>
            <img src={img3} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: pos3, display: "block" }} />
          </Box>
        </Box>

        <Box sx={{
          position: "absolute", inset: 0,
          clipPath: "polygon(47.7% 0%, 48.7% 0%, 34.3% 100%, 33.3% 100%)",
          backgroundColor: "#FFFFFF", zIndex: 5, pointerEvents: "none",
        }} />
        <Box sx={{
          position: "absolute", inset: 0,
          clipPath: "polygon(81.1% 0%, 82.1% 0%, 67.7% 100%, 66.7% 100%)",
          backgroundColor: "#FFFFFF", zIndex: 5, pointerEvents: "none",
        }} />
      </Box>

      {/* ══ LEFT TEXT PANEL ══════════════════════════════════════════════════ */}
      <Box
        sx={{
          position: "absolute",
          left: 0, top: 0,
          width: "43%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          pl: "clamp(40px, 4.4vw, 80px)",
          zIndex: 6,
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontFamily: "Red Hat Display, sans-serif",
            fontWeight: 800,
            fontSize: "clamp(44px, 4.5vw, 76px)",
            lineHeight: 1.1,
            color: "#FFFFFF",
            mb: "clamp(16px, 1.8vw, 28px)",
            maxWidth: "clamp(400px, 34vw, 580px)",
          }}
        >
          {slideData.mainTitle.split("\n").map((line, i, arr) => (
            <React.Fragment key={i}>
              {line}
              {i < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </Typography>

        {slideData.description && (
          <Typography sx={{
            fontFamily: "Red Hat Display, sans-serif",
            fontWeight: 600,
            fontSize: "clamp(14px, 1.1vw, 19px)",
            lineHeight: 1.6,
            color: "rgba(255,255,255,0.80)",
            mb: "clamp(28px, 3.2vw, 50px)",
            maxWidth: "clamp(340px, 30vw, 500px)",
          }}>
            {slideData.description}
          </Typography>
        )}

        <Box
          onClick={() => onDonate(slideData)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "clamp(160px, 13vw, 210px)",
            height: "clamp(48px, 4vw, 62px)",
            borderRadius: "9px",
            background: "linear-gradient(94.58deg, #F41E1E 0.28%, #F04951 95.94%)",
            cursor: "pointer",
            transition: "opacity 0.2s",
            "&:hover": { opacity: 0.87 },
            alignSelf: "flex-start",
          }}
        >
          <Typography sx={{
            fontFamily: "Red Hat Display, sans-serif",
            fontWeight: 600,
            fontSize: "clamp(17px, 1.4vw, 22px)",
            color: "#fff",
          }}>
            Donate Now
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ─── One mobile slide (text area + 3 diagonal image strips) ───────────────────
const MobileSlide = ({ slideData, onDonate, sx }) => {
  const img1 = slideData.images?.[0] || FALLBACK_IMGS[0];
  const img2 = slideData.images?.[1] || FALLBACK_IMGS[1];
  const img3 = slideData.images?.[2] || FALLBACK_IMGS[2];
  const mImg1 = slideData.mobileImages?.[0] || img1;
  const mImg2 = slideData.mobileImages?.[1] || img2;
  const mImg3 = slideData.mobileImages?.[2] || img3;

  return (
    <Box sx={{ width: "100%", ...sx }}>
      {/* ── Text area ──────────────────────────────────────────────────────── */}
      <Box sx={{ px: "24px", pt: "28px", pb: "28px" }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: "Red Hat Display, sans-serif",
            fontWeight: 800,
            fontSize: "32px",
            lineHeight: 1.15,
            color: "#FFFFFF",
            mb: "14px",
          }}
        >
          {slideData.mainTitle.split("\n").map((line, i, arr) => (
            <React.Fragment key={i}>
              {line}
              {i < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </Typography>

        {slideData.description && (
          <Typography sx={{
            fontFamily: "Red Hat Display, sans-serif",
            fontWeight: 500,
            fontSize: "13px",
            lineHeight: 1.6,
            color: "rgba(255,255,255,0.75)",
            mb: "22px",
          }}>
            {slideData.description}
          </Typography>
        )}

        <Box
          onClick={() => onDonate(slideData)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "148px",
            height: "44px",
            borderRadius: "9px",
            background: "linear-gradient(94.58deg, #F41E1E 0.28%, #F04951 95.94%)",
            cursor: "pointer",
            transition: "opacity 0.2s",
            "&:hover": { opacity: 0.87 },
          }}
        >
          <Typography sx={{
            fontFamily: "Red Hat Display, sans-serif",
            fontWeight: 600,
            fontSize: "15px",
            color: "#fff",
          }}>
            Donate Now
          </Typography>
        </Box>
      </Box>

      {/* ── Three horizontal diagonal image strips ──────────────────────── */}
      <Box sx={{ position: "relative", width: "100%", aspectRatio: "390 / 480" }}>
        <Box sx={{
          position: "absolute", inset: 0,
          clipPath: "polygon(0% 8.5%, 100% 0%, 100% 30.5%, 0% 39%)",
          overflow: "hidden",
        }}>
          <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "39%" }}>
            <img src={mImg1} alt="" style={{ width: "100%", height: "100%", objectFit: "fill", display: "block" }} />
          </Box>
        </Box>

        <Box sx={{
          position: "absolute", inset: 0,
          clipPath: "polygon(0% 39%, 100% 30.5%, 100% 31.3%, 0% 39.8%)",
          backgroundColor: "#FFFFFF", zIndex: 5, pointerEvents: "none",
        }} />

        <Box sx={{
          position: "absolute", inset: 0,
          clipPath: "polygon(0% 39%, 100% 30.5%, 100% 61%, 0% 69.5%)",
          overflow: "hidden",
        }}>
          <Box sx={{ position: "absolute", top: "30.5%", left: 0, width: "100%", height: "39%" }}>
            <img src={mImg2} alt="" style={{ width: "100%", height: "100%", objectFit: "fill", display: "block" }} />
          </Box>
        </Box>

        <Box sx={{
          position: "absolute", inset: 0,
          clipPath: "polygon(0% 69.5%, 100% 61%, 100% 61.8%, 0% 70.3%)",
          backgroundColor: "#FFFFFF", zIndex: 5, pointerEvents: "none",
        }} />

        <Box sx={{
          position: "absolute", inset: 0,
          clipPath: "polygon(0% 69.5%, 100% 61%, 100% 100%, 0% 100%)",
          overflow: "hidden",
        }}>
          <Box sx={{ position: "absolute", top: "61%", left: 0, width: "100%", height: "39%" }}>
            <img src={mImg3} alt="" style={{ width: "100%", height: "100%", objectFit: "fill", display: "block" }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const HeroSection = () => {
  const navigate = useNavigate();
  const { slides, isInitialLoading } = useHeroSlides();
  const n = slides.length;

  const [idx, setIdx]                 = useState(0);
  const [outgoing, setOutgoing]       = useState(null); // { idx, direction }
  const [touchStart, setTouchStart]   = useState(null);
  const [touchEnd, setTouchEnd]       = useState(null);

  const goTo = (i, direction) => {
    if (i === idx || n <= 1) return;
    setOutgoing({ idx, direction });
    setIdx(i);
  };
  const next = () => goTo((idx + 1) % n, 1);
  const prev = () => goTo((idx - 1 + n) % n, -1);
  const goToDot = (i) => goTo(i, i > idx ? 1 : -1);

  // Clear the outgoing slide once the push animation finishes
  useEffect(() => {
    if (!outgoing) return;
    const t = setTimeout(() => setOutgoing(null), SLIDE_DURATION);
    return () => clearTimeout(t);
  }, [outgoing]);

  // Auto-advance every 6 s
  useEffect(() => {
    if (n <= 1) return;
    const t = setTimeout(next, 6000);
    return () => clearTimeout(t);
  }, [idx, n]);

  const handleDonate = (slideData) => {
    if (!slideData.link) { navigate("/campaigns"); return; }
    try {
      const url = new URL(slideData.link);
      if (url.hostname.includes("truehopefoundation")) navigate(url.pathname);
      else window.location.href = slideData.link;
    } catch {
      navigate(slideData.link.startsWith("/") ? slideData.link : `/${slideData.link}`);
    }
  };

  const onTouchStart = (e) => { setTouchEnd(null); setTouchStart(e.targetTouches[0].clientX); };
  const onTouchMove  = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd   = () => {
    if (!touchStart || !touchEnd) return;
    if (touchStart - touchEnd > 50) next();
    if (touchStart - touchEnd < -50) prev();
  };

  if (isInitialLoading) return <HeroSkeleton />;

  const slide = slides[idx];
  const outAnim = outgoing?.direction === 1 ? pushOutToLeft : pushOutToRight;
  const inAnim  = outgoing?.direction === 1 ? pushInFromRight : pushInFromLeft;

  // Shared dots component
  const Dots = ({ bottom = "24px", size = 10, activeW = 28 }) => (
    <Box sx={{
      position: "absolute",
      bottom,
      left: "50%",
      transform: "translateX(-50%)",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      zIndex: 10,
      px: "14px",
      py: "8px",
      borderRadius: "20px",
      backdropFilter: "blur(8px)",
      backgroundColor: "rgba(0,0,0,0.25)",
    }}>
      {slides.map((_, i) => (
        <Box
          key={i}
          onClick={() => goToDot(i)}
          sx={{
            width: i === idx ? `${activeW}px` : `${size}px`,
            height: `${size}px`,
            borderRadius: `${size / 2}px`,
            backgroundColor: i === idx ? "#F04951" : "#FFFFFF",
            boxShadow: i === idx ? "none" : "0 0 0 2px rgba(255,255,255,0.8)",
            cursor: "pointer",
            transition: "all 0.3s ease",
            flexShrink: 0,
          }}
        />
      ))}
    </Box>
  );

  return (
    <>
      {/* ══════════════════════════════════════════════════════════════════════
          DESKTOP LAYOUT  (md and up) — unchanged
         ══════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "relative",
          width: "94.44%",
          margin: "15px auto",
        }}
      >
      <Box
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        sx={{
          position: "relative",
          width: "100%",
          height: "649px",
          borderRadius: "20px",
          overflow: "hidden",
          backgroundColor: "#2F2E2E",
          userSelect: "none",
        }}
      >
        {outgoing && (
          <DesktopSlide
            key={`out-${outgoing.idx}`}
            slideData={slides[outgoing.idx]}
            onDonate={handleDonate}
            sx={{ animation: `${outAnim} ${SLIDE_DURATION}ms ease forwards`, zIndex: 2 }}
          />
        )}
        <DesktopSlide
          key={`in-${idx}`}
          slideData={slide}
          onDonate={handleDonate}
          sx={outgoing ? { animation: `${inAnim} ${SLIDE_DURATION}ms ease forwards`, zIndex: 3 } : { zIndex: 3 }}
        />

        <Dots />
      </Box>

      {n > 1 && (
        <>
          <Box onClick={prev} aria-label="Previous slide" sx={{
            position: "absolute", left: 0, top: "50%",
            transform: "translate(-50%, -50%)", cursor: "pointer",
            zIndex: 10, transition: "transform 0.2s",
            "&:hover": { transform: "translate(-50%, -50%) scale(1.05)" },
          }}>
            <ArrowLeft />
          </Box>
          <Box onClick={next} aria-label="Next slide" sx={{
            position: "absolute", right: 0, top: "50%",
            transform: "translate(50%, -50%)", cursor: "pointer",
            zIndex: 10, transition: "transform 0.2s",
            "&:hover": { transform: "translate(50%, -50%) scale(1.05)" },
          }}>
            <ArrowRight />
          </Box>
        </>
      )}
      </Box>

      {/* ══════════════════════════════════════════════════════════════════════
          MOBILE LAYOUT  (xs / sm — below md)
          Figma: UF1WTWeHNgvZW7BhDaeZOU  node 271:2417
          Layout: dark text area on top + 3 diagonal horizontal image strips below
         ══════════════════════════════════════════════════════════════════════ */}
      <Box
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        sx={{
          display: { xs: "block", md: "none" },
          position: "relative",
          width: "calc(100% - 20px)",
          margin: "15px auto",
          borderRadius: "14px",
          overflow: "hidden",
          backgroundColor: "#2F2E2E",
          userSelect: "none",
        }}
      >
        <MobileSlide
          key={`m-in-${idx}`}
          slideData={slide}
          onDonate={handleDonate}
          sx={outgoing ? { position: "relative", animation: `${inAnim} ${SLIDE_DURATION}ms ease forwards`, zIndex: 1 } : { position: "relative", zIndex: 1 }}
        />
        {outgoing && (
          <MobileSlide
            key={`m-out-${outgoing.idx}`}
            slideData={slides[outgoing.idx]}
            onDonate={handleDonate}
            sx={{ position: "absolute", inset: 0, animation: `${outAnim} ${SLIDE_DURATION}ms ease forwards`, zIndex: 2 }}
          />
        )}

        <Dots bottom="14px" size={8} activeW={24} />
      </Box>
    </>
  );
};

export default HeroSection;
